// Facturas pendientes del taller — Taller → Operación → Facturas pendientes (963).
// Comprobantes de la caja del taller que el SRI aún no autorizó (entregas,
// planes y usados): situación, motivo del SRI y reintento sin salir del taller.
// El descarte de un comprobante sigue en Financiero → Bandeja de emitidos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, FileWarning, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { useTabsStore } from '@/stores/tabs.store';
import { tallerRpc, QK, money, fmtFechaHora, useSort, SortTh } from './shared';

interface Factura {
  ticket_id: string; venta: string; fecha: string; cliente: string | null; total: number; serie: string | null; estado: string;
  situacion: 'pendiente' | 'en_curso' | 'autorizado' | 'problema' | 'anulado'; mensaje: string | null; horas: number; origen: string | null; orden_id: string | null;
}
const SIT: Record<Factura['situacion'], { label: string; cls: string }> = {
  pendiente: { label: 'Por emitir', cls: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200' },
  en_curso: { label: 'En curso', cls: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300' },
  problema: { label: 'Con problema', cls: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300' },
  autorizado: { label: 'Autorizada', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300' },
  anulado: { label: 'Anulada', cls: 'bg-muted text-muted-foreground' },
};

export function FacturasTaller() {
  const qc = useQueryClient();
  const openTab = useTabsStore((s) => s.openTab);
  const [todas, setTodas] = useState(false);
  const { data, isLoading } = useQuery<{ certificado: boolean; facturas: Factura[] }>({
    queryKey: [QK, 'facturas', todas], queryFn: () => tallerRpc('facturas_pendientes', { _todas: todas }), refetchInterval: 30000,
  });
  const reintentar = useMutation({
    mutationFn: (id: string) => tallerRpc('factura_reintentar', { _ticket: id }),
    onSuccess: () => { toast.success('Comprobante enviado de nuevo al SRI'); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const filas = data?.facturas ?? [];
  const sort = useSort<Factura>(filas, (r, k) => (k === 'venta' ? r.venta : k === 'fecha' ? r.fecha : k === 'total' ? Number(r.total) : k === 'horas' ? r.horas : k === 'sit' ? r.situacion : null));
  const total = filas.reduce((a, r) => a + Number(r.total), 0);
  const problema = filas.filter((r) => r.situacion === 'problema').length;
  const atascadas = filas.filter((r) => r.situacion === 'en_curso' && r.horas >= 2).length;
  const abrirOrden = (id: string) => {
    try { sessionStorage.setItem('taller_abrir_orden', id); } catch { /* sin almacenamiento */ }
    openTab({ id: '/taller/ordenes', title: 'Órdenes', path: '/taller/ordenes' });
  };

  return (
    <div className="space-y-3 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold"><FileWarning className="h-5 w-5" /> Facturas pendientes del taller</h1>
          <p className="text-sm text-muted-foreground">Comprobantes de la caja del taller que el SRI aún no autorizó: entregas, planes y usados.</p>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={todas} onChange={(e) => setTodas(e.target.checked)} /> Ver también las autorizadas (60 días)</label>
      </div>

      {data && !data.certificado && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>La empresa no tiene firma electrónica (.p12): las facturas quedan «por emitir» y no llegan al SRI. La configura el administrador en Financiero → Configuración de Emisión; después, reintente desde aquí.</span>
        </div>
      )}
      {atascadas > 0 && (
        <div className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          {atascadas} comprobante(s) llevan 2 horas o más «en curso»: el servicio de emisión podría estar detenido. Avise a soporte.
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[[filas.filter((r) => r.situacion !== 'autorizado').length, 'Sin autorizar'], [problema, 'Con problema (rechazo / error)'], [atascadas, 'Atascadas en curso'], [money(total), 'Monto']].map(([n, t]) => (
          <div key={String(t)} className="rounded-lg border p-3"><div className="text-xl font-semibold tabular-nums">{n}</div><div className="text-xs text-muted-foreground">{t}</div></div>
        ))}
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th><SortTh col="venta" sort={sort}>Venta</SortTh><SortTh col="fecha" sort={sort}>Fecha</SortTh>
              <th className="py-2 pr-2">Origen</th><th className="py-2 pr-2">Cliente</th><SortTh col="total" sort={sort} className="text-right">Total</SortTh>
              <SortTh col="sit" sort={sort}>Situación</SortTh><th className="py-2 pr-2">Motivo del SRI</th><SortTh col="horas" sort={sort} className="text-right">Horas</SortTh><th /></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Cargando…</td></tr>}
              {!isLoading && filas.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Todas las facturas del taller están autorizadas.</td></tr>}
              {sort.sorted.map((r, i) => (
                <tr key={r.ticket_id} className="border-b last:border-0">
                  <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-2 font-mono">{r.venta}{r.serie && <div className="text-[11px] text-muted-foreground">{r.serie}</div>}</td>
                  <td className="py-1.5 pr-2">{fmtFechaHora(r.fecha)}</td>
                  <td className="py-1.5 pr-2">{r.orden_id ? <button type="button" className="text-primary hover:underline" onClick={() => abrirOrden(r.orden_id!)}>{r.origen}</button> : r.origen}</td>
                  <td className="py-1.5 pr-2">{r.cliente ?? '—'}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{money(Number(r.total))}</td>
                  <td className="py-1.5 pr-2"><span className={`rounded-full px-2 py-0.5 text-xs ${SIT[r.situacion]?.cls ?? ''}`}>{SIT[r.situacion]?.label ?? r.estado}</span></td>
                  <td className="max-w-xs py-1.5 pr-2 text-xs text-muted-foreground" title={r.mensaje ?? ''}>{r.mensaje ? r.mensaje.slice(0, 120) : '—'}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{r.horas}</td>
                  <td className="py-1.5 pr-3 text-right">
                    {['pendiente', 'problema'].includes(r.situacion) &&
                      <Button size="sm" variant="outline" isLoading={reintentar.isPending && reintentar.variables === r.ticket_id} onClick={() => reintentar.mutate(r.ticket_id)}>
                        <RefreshCw className="mr-1 h-3.5 w-3.5" /> Reintentar</Button>}
                  </td>
                </tr>))}
            </tbody>
            {filas.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={5} className="py-2 pl-3">{filas.length} comprobante(s)</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(total)}</td><td colSpan={4} /></tr></tfoot>}
          </table>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">Un comprobante con problema que no debe emitirse se descarta en Financiero → Bandeja de emitidos (administrador). Uno autorizado no se descarta: se anula con nota de crédito.</p>
    </div>
  );
}
